import React from 'react'
import { Portal, useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { Filter } from 'iconsax-react'

import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'

import {
  OnwerFilterTypes,
  setOwnerFilters,
  setOwnerId,
  ContactsTableFilters,
  resetFilters,
  setTypesFilter,
  setStatusFilter
} from '@SonarCX/slices/contactsTable'
import MenuSection from '../../Tasks/Tabs/AllTasksTab/Filters/MenuSection'
import Button from '@SonarCX/components/atoms/Button2'
import UserSearch from '../../Tasks/Tabs/AllTasksTab/Filters/UserSearch'
import SideModal from '@SonarCX/components/atoms/SideModal'

import { selectUserType } from '@SonarCX/slices/userType'
import { selectUserStatus } from '@SonarCX/slices/userStatus'
import { getIDs, getNames } from '../../Tasks/Tabs/AllTasksTab/helpers/parse'

import useStyles from './styles'

const OWNER_OPTIONS = [
  OnwerFilterTypes.ALL,
  OnwerFilterTypes.I_OWN,
  OnwerFilterTypes.FILTER_BY_USER
]

interface FiltersProps {
  filter: ContactsTableFilters
  children?: React.ReactNode
}

const Filters: React.FC<FiltersProps> = ({ filter, children }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()

  const { types } = useAppSelector(selectUserType)
  const { statuses } = useAppSelector(selectUserStatus)
  const { user } = useEnvironment()
  const [open, setOpen] = React.useState(false)
  const externalTypes = React.useMemo(
    () => types.filter(x => x.external),
    [types]
  )

  const title = React.useMemo(() => {
    let total = 0
    if (filter.owner.ownerOption !== OnwerFilterTypes.ALL) {
      total += 1
    }
    if (filter.status !== 'null') {
      total += 1
    }
    if (filter.type !== 'null') {
      total += 1
    }
    if (total === 0) return 'No Filters'
    return `${total} FILTER${total === 1 ? '' : 'S'}`
  }, [filter])

  const handleChange = React.useCallback(
    key => val => {
      if (key === 'status') {
        dispatch(setStatusFilter(val))
      } else if (key === 'types') {
        dispatch(setTypesFilter(val))
      } else {
        if (val === OnwerFilterTypes.I_OWN) {
          dispatch(setOwnerFilters({ ownerId: user?.id, ownerOption: val }))
        } else if (val === OnwerFilterTypes.ALL) {
          dispatch(setOwnerFilters({ ownerId: 'null', ownerOption: val }))
        } else {
          dispatch(setOwnerFilters({ ownerId: 'null', ownerOption: val }))
        }
      }
    },
    [user, dispatch]
  )

  const handleUserChange = React.useCallback(
    val => {
      dispatch(setOwnerId(val))
    },
    [dispatch]
  )

  const clearAll = React.useCallback(() => {
    dispatch(resetFilters())
  }, [dispatch])

  React.useEffect(() => {
    if (
      filter.owner.ownerOption === OnwerFilterTypes.I_OWN &&
      filter.owner.ownerId === 'null'
    ) {
      dispatch(setOwnerId(user?.id))
    }
  }, [filter, dispatch, user])

  const classes = useStyles()

  return (
    <>
      <div onClick={() => setOpen(old => !old)} className={classes.filter}>
        <Filter className='icon' size={theme.size.base} variant='Outline' />

        <div className='text'>{title}</div>

        {children && (
          <span onClick={e => e.stopPropagation()} className={classes.children}>
            {children}
          </span>
        )}
      </div>

      {open && (
        <Portal>
          <SideModal
            sidemodalKey='filters'
            title={title}
            onClose={() => setOpen(false)}
            rootClass={classes.root}
          >
            <MenuSection
              title='Created by'
              options={OWNER_OPTIONS}
              value={filter.owner.ownerOption}
              setValue={handleChange('owners')}
              hideNullOption
              noBorder
              testId='filter-owner'
            >
              {filter.owner.ownerOption === OnwerFilterTypes.FILTER_BY_USER && (
                <UserSearch
                  userID={null}
                  value={filter.owner.ownerId}
                  setValue={handleUserChange}
                  disablePortal={false}
                  hideMe={false}
                />
              )}
            </MenuSection>

            {/* @ts-ignore */}
            <MenuSection
              title='Types'
              options={getIDs(externalTypes)}
              value={filter.type}
              setValue={handleChange('types')}
              transform={getNames(externalTypes)}
              unassignedOpt='All Types'
            />

            {/* @ts-ignore */}
            <MenuSection
              title='Status'
              options={getIDs(statuses)}
              value={filter.status}
              setValue={handleChange('status')}
              transform={getNames(statuses)}
            />

            <Button onClick={clearAll} className={classes.button}>
              CLEAR ALL
            </Button>
          </SideModal>
        </Portal>
      )}
    </>
  )
}

export default Filters
